// Admin (Reviews tab) — moderate guest photos: approve / reject, edit caption.
import React, { useCallback, useEffect, useState } from 'react';
import { Camera, Check, Loader2, RefreshCw, X } from 'lucide-react';
import { apiCall } from './_shared';

const GuestPhotosModeration = () => {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { setData(await apiCall(`/api/admin/guest-photos?status=${status}`)); } catch (e) { setData({ items: [], counts: {}, error: String(e.message || e) }); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, st, caption) => {
    setBusy(id);
    try { await apiCall(`/api/admin/guest-photos/${id}`, { method: 'PUT', body: JSON.stringify({ status: st, caption }) }); await load(); } catch (e) { alert(String(e.message || e)); }
    setBusy('');
  };
  const c = data?.counts || {};
  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="guest-photos-moderation">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900 inline-flex items-center gap-2"><Camera className="w-4 h-4 text-sky-600" /> Guest photo wall</h3>
          <p className="text-xs text-slate-500 mt-1">Guests upload via the link in the thank-you e-mail. Only approved photos appear on the home page (the wall shows once 3+ are approved).</p>
        </div>
        <div className="flex items-center gap-2">
          {['pending', 'approved', 'rejected'].map((s) => (
            <button key={s} type="button" onClick={() => setStatus(s)} className={`text-xs px-3 py-1.5 rounded-full border ${status === s ? 'bg-slate-900 text-white border-slate-900' : 'hover:bg-slate-50'}`} data-testid={`guest-photos-tab-${s}`}>{s} {c[s] != null ? `(${c[s]})` : ''}</button>
          ))}
          <button type="button" onClick={load} className="text-xs border rounded-full px-2 py-1.5 hover:bg-slate-50" data-testid="guest-photos-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {!data ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : data.error ? <p className="text-xs text-rose-700">{data.error}</p> : data.items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="guest-photos-empty">No {status} photos.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="guest-photos-grid">
          {data.items.map((p) => (
            <div key={p.id} className="border rounded-xl overflow-hidden bg-slate-50" data-testid={`guest-photo-item-${p.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-full h-40 object-cover" />
              <div className="p-2 text-xs space-y-1">
                <div className="font-semibold text-slate-800 truncate">{p.guest_name || 'Guest'}{p.country ? ` · ${p.country}` : ''} <span className="text-slate-400 font-normal">· {(p.created_at || '').slice(0, 10)}</span></div>
                <input defaultValue={p.caption || ''} maxLength={140} placeholder="Caption" className="w-full border rounded px-2 py-1" data-testid={`guest-photo-caption-${p.id}`} id={`cap-${p.id}`} />
                <div className="flex gap-1 pt-1">
                  {p.status !== 'approved' && <button type="button" disabled={busy === p.id} onClick={() => act(p.id, 'approved', document.getElementById(`cap-${p.id}`).value)} className="flex-1 inline-flex items-center justify-center gap-1 bg-emerald-600 text-white rounded px-2 py-1 disabled:opacity-50" data-testid={`guest-photo-approve-${p.id}`}><Check className="w-3 h-3" /> Approve</button>}
                  {p.status !== 'rejected' && <button type="button" disabled={busy === p.id} onClick={() => act(p.id, 'rejected')} className="flex-1 inline-flex items-center justify-center gap-1 border rounded px-2 py-1 text-rose-700 disabled:opacity-50" data-testid={`guest-photo-reject-${p.id}`}><X className="w-3 h-3" /> Reject</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuestPhotosModeration;
